import type {
  CardElementError,
  CardElementEvent,
  CardElementRef,
} from '@flywire/internal-react-elements';
import {
  trackAcceptOfferRedirection,
  trackCardElementEventForSurcharge,
  trackChangeCardRedirection,
  trackChangePaymentMethod,
  trackChooseCurrency,
  trackClickPay,
  trackEditCurrency,
  trackExpandPriceBreakdown,
  trackHoverSurchargeTooltip,
  trackTryAgain,
} from 'actions/tracker/surchargeV2';
import { BackButton } from 'components/BackButton/BackButton';
import { CardForm } from 'components/CardForm/CardForm';
import { ErrorComponent as CheckoutErrorComponent } from 'components/Error/Error';
import { PausedError } from 'components/PausedError/PausedError';
import {
  Captcha,
  type CaptchaRef,
} from 'components/thirdParty/Captcha/Captcha';
import { Heading } from 'components/ui/Heading/Heading';
import { Spinner } from 'components/ui/Spinner/Spinner';
import { PAUSED_ERROR } from 'constants/errors';
import type { HttpError } from 'constants/http';
import { useConfirmCheckoutSession } from 'hooks/useConfirmCheckoutSession/useConfirmCheckoutSession';
import { useCreateCheckoutSession } from 'hooks/useCreateCheckoutSession/useCreateCheckoutSession';
import { useExperimentTrack } from 'hooks/useExperiments/useExperimentTrack';
import { useRef } from 'react';
import { useSelector } from 'react-redux';
import { getSenderEmail } from 'selectors/entities/order/order';
import { getSelectedOffer } from 'selectors/ui/ui';
import { useDispatch } from 'store/configureStore';
import { useTranslations } from 'utils';
import { errorNotifier } from 'utils/errorNotifier/errorNotifier';
import { isHttpErrorWithParsedBody } from 'utils/errors/errors';
import { Success } from './Success';
import { SurchargeError as ErrorComponent } from './SurchargeError';
import { useCreditCardPaymentFormState } from './useCreditCardPaymentFormState';

const EXPERIMENT_EVENT_NAME = 'surcharge_payment_created';

type CardFormProps = {
  goToStep: (step: string) => void;
  onPrev: () => void;
};

export const CreditCardPaymentFormV2 = ({
  goToStep,
  onPrev,
}: CardFormProps) => {
  const i18n = useTranslations();
  const captchaRef = useRef<CaptchaRef>(null);
  const ref = useRef<CardElementRef>(null);
  const dispatch = useDispatch();

  const { state, setLoading, setShowErrorComponent, setSuccess } =
    useCreditCardPaymentFormState();
  const { paymentId, isError } = state;
  const trackExperimentEvent = useExperimentTrack();
  const senderEmail = useSelector(getSenderEmail);

  const {
    price: { currency },
  } = useSelector(getSelectedOffer);

  const {
    mutateAsync: createSession,
    error: createSessionError,
    sessionId,
    isPending: isCreatingSession,
  } = useCreateCheckoutSession({
    createSessionOnMount: false,
    sessionData: { sessionType: 'one_off_for_cards' },
  });

  const { mutateAsync: confirmSession } = useConfirmCheckoutSession();

  const _createSession = async (args: Parameters<typeof createSession>[0]) => {
    try {
      await createSession(args);
    } catch {
      // do nothing we read error from the hook
    }
  };

  const submit = async () => {
    dispatch(
      trackClickPay({
        sessionId,
      }),
    );
    await ref.current?.submit();
  };

  const onSuccess = async () => {
    try {
      const confirmResponse = await confirmSession(sessionId as string);

      setSuccess(confirmResponse.paymentId as string);
      trackExperimentEvent({
        eventName: EXPERIMENT_EVENT_NAME,
        userEmail: senderEmail as string,
        eventProperties: {
          decision: confirmResponse.decision,
        },
      });
    } catch (error) {
      console.error('Session confirmation failed:', error);
      setShowErrorComponent(true);
    } finally {
      captchaRef.current?.reset();
    }
  };

  const onErrorRetry = async () => {
    dispatch(trackTryAgain({ sessionId }));
    try {
      if (captchaRef.current?.isEnabled()) {
        captchaRef.current?.execute();
      } else {
        await _createSession({});
        setShowErrorComponent(false);
      }
    } catch (e) {
      errorNotifier.notify(e);
    } finally {
      captchaRef.current?.reset();
    }
  };

  const onErrorChangePaymentMethod = () => {
    dispatch(trackChangePaymentMethod({ sessionId }));
    goToStep('paymentOptions');
  };

  const onError = (e: CardElementError) => {
    if (e.type !== 'invalid_card_info') {
      setShowErrorComponent(true);
    }

    captchaRef.current?.reset();
  };

  const handleCaptchaReady = async () => {
    if (captchaRef.current?.isEnabled()) {
      captchaRef.current?.execute();
    } else {
      await _createSession({});
    }
  };

  const handleCaptchaVerify = async (token?: string) => {
    await _createSession({ captchaResponse: token });
    setShowErrorComponent(false);
  };

  const handleCaptchaChallengeClose = () => {
    captchaRef.current?.execute();
  };

  const handleCaptchaError = () => {
    setShowErrorComponent(true);
  };

  const renderCardElementContent = () => {
    if (isCreatingSession) {
      return <Spinner />;
    }

    if (isPausedError(createSessionError)) {
      return <PausedError />;
    }

    if (createSessionError) {
      return (
        <CheckoutErrorComponent errorKey="surcharge.card.createSessionError" />
      );
    }
    if (isError) {
      return (
        <ErrorComponent
          onRetry={onErrorRetry}
          onChangePaymentMethod={onErrorChangePaymentMethod}
        />
      );
    }

    if (paymentId) {
      return <Success paymentId={paymentId} status={'success'} />;
    }

    return (
      <div>
        <BackButton onClick={onPrev} />
        <Heading as="h2" size="large" className="fontWeight-semiBold">
          {i18n.t('cardForm.title')}
        </Heading>
        <>
          {state.status === 'loading' ? (
            <Spinner data-testid="surchargeFormSpinner" />
          ) : (
            <>
              <CardForm.Form
                onSubmit={submit}
                buttonLabel={i18n.t('cardForm.pay') as string}
                withAmount={true}
              />
            </>
          )}
        </>
      </div>
    );
  };
  const onEvent = (event: CardElementEvent) => {
    if (event.type === 'offer_accepted' && event.extra.redirected) {
      dispatch(
        trackAcceptOfferRedirection({
          sessionId,
          currency: event.extra.offer.price.currency.code,
          decision: {
            action: event.extra.offer.surcharge?.decision || 'charge',
            fee: event.extra.offer.surcharge?.percentage || 0,
          },
        }),
      );
    } else if (event.type === 'offer_accepted' && event.extra.twoCurrencies) {
      dispatch(
        trackChooseCurrency({
          sessionId,
          currency: event.extra.offer.price.currency.code,
          decision: {
            action: event.extra.offer.surcharge?.decision || 'charge',
            fee: event.extra.offer.surcharge?.percentage || 0,
          },
        }),
      );
    } else if (event.type === 'offer_rejected') {
      dispatch(
        trackChangeCardRedirection({
          sessionId,
        }),
      );
    } else if (event.type === 'selected_offer_reset') {
      dispatch(
        trackEditCurrency({
          sessionId,
        }),
      );
    } else if (event.type === 'surcharge_tooltip_shown') {
      dispatch(
        trackHoverSurchargeTooltip({
          sessionId,
        }),
      );
    } else if (event.type === 'price_breakdown_shown') {
      dispatch(
        trackExpandPriceBreakdown({
          sessionId,
          priceType: event.extra.type,
        }),
      );
    } else {
      dispatch(
        trackCardElementEventForSurcharge({
          type: event.type,
          content: event,
          sessionId,
        }),
      );
    }
  };

  return (
    <>
      <Captcha
        ref={captchaRef}
        onReady={handleCaptchaReady}
        onVerify={handleCaptchaVerify}
        onError={handleCaptchaError}
        onClose={handleCaptchaChallengeClose}
        data-testid="surchargeV2-Captcha"
      />
      <CardForm
        cardFormRef={ref}
        sessionId={sessionId as string}
        onSuccess={onSuccess}
        onError={onError}
        onStartLoading={() => setLoading(true)}
        onEndLoading={() => setLoading(false)}
        onEvent={onEvent}
        currency={currency as string}
      >
        {renderCardElementContent()}
      </CardForm>
    </>
  );
};

const isPausedError = (error: unknown): error is HttpError => {
  const httpError = error as HttpError;
  return (
    isHttpErrorWithParsedBody(error) &&
    !!httpError.parsedBody?.errors &&
    Array.isArray(httpError.parsedBody.errors) &&
    'type' in (httpError.parsedBody.errors[0] || {}) &&
    httpError.parsedBody.errors[0].type === PAUSED_ERROR
  );
};
